import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Modal,
  Button,
  Row,
  Col,
  Space,
  Typography,
  Steps,
  Alert,
  Card,
  Tag,
  List,
} from "antd";
import {
  CheckOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  BuildOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
  ArrowLeftOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";
import trans from "../../utils";
import ImageMapViewer from "./ImageMapViewer";
import { InputSelect } from "../Helpers/Input/RefactoredInput";
import {
  useGetPOIZonesByParentIdQuery,
  useGetPOIProjectsQuery,
  useGetPOIBuildingsQuery,
} from "../../Redux/service/POIS/POIs";
import { useGetFloorplanQuery } from "../../Redux/service/Snag/Snag";
import "./SnagLocationSelectorModal.css";
import PopupTitle from "../Helpers/Popup/PopupParts/PopupTitle";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text, Paragraph } = Typography;

/** On mobile, don't render the map if dimensions exceed these (prevents crash/sluggishness) */
const MOBILE_MAX_MAP_PIXELS = 2000000; // ~1.4k x 1.4k
const MOBILE_MAX_MAP_SIDE = 2000;

/**
 * Enhanced SnagLocationSelectorModal Component
 * Flow: Project → Zone (Building) → Building (Level) → Space Selection
 * Shows single map that updates based on selection with zoom animation
 * Enhanced with progress indicators, help text, and visual guidance
 */
const SnagLocationSelectorModal = ({
  open,
  onClose,
  onConfirm,
  preselectedValues = null,
}) => {
  const isMobile = useIsMobile();
  const [projectId, setProjectId] = useState(
    preselectedValues?.project_id || "",
  );
  const [zoneId, setZoneId] = useState(preselectedValues?.zone_id || "");
  const [buildingId, setBuildingId] = useState(
    preselectedValues?.building_id || "",
  );
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  /** Mobile only: when true, show full-screen map/list to select space (map not rendered in form) */
  const [mobileMapViewOpen, setMobileMapViewOpen] = useState(false);

  // Fetch projects
  const { data: projectsData } = useGetPOIProjectsQuery();

  // Fetch zones based on selected project
  const { data: zonesData, isFetching: zonesLoading } =
    useGetPOIZonesByParentIdQuery(
      { id: projectId },
      {
        skip: !projectId,
      },
    );

  // Fetch buildings based on selected zone
  const { data: buildingsData, isFetching: buildingsLoading } =
    useGetPOIBuildingsQuery(
      { id: zoneId },
      {
        skip: !zoneId,
      },
    );

  // Determine which map to show
  const getCurrentMapId = () => {
    if (buildingId) return buildingId;
    if (zoneId) return zoneId;
    if (projectId) return projectId;
    return null;
  };

  const currentMapId = getCurrentMapId();

  // Fetch current map
  const { data: currentMapData, isFetching: currentMapLoading } =
    useGetFloorplanQuery(currentMapId, {
      skip: !currentMapId,
    });

  // Trigger animation when map changes
  useEffect(() => {
    if (currentMapId) {
      setMapKey((prev) => prev + 1);
    }
  }, [currentMapId]);

  // Reset dependent fields when parent changes
  useEffect(() => {
    if (!projectId) {
      setZoneId("");
      setBuildingId("");
      setSelectedSpace(null);
      setMobileMapViewOpen(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!zoneId) {
      setBuildingId("");
      setSelectedSpace(null);
      setMobileMapViewOpen(false);
    }
  }, [zoneId]);

  useEffect(() => {
    if (!buildingId) {
      setSelectedSpace(null);
      setMobileMapViewOpen(false);
    }
  }, [buildingId]);

  // Update state when preselectedValues change
  useEffect(() => {
    if (open && preselectedValues) {
      setProjectId(preselectedValues.project_id || "");
      setZoneId(preselectedValues.zone_id || "");
      setBuildingId(preselectedValues.building_id || "");
      setSelectedSpace(null);
    }
  }, [open, preselectedValues]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setMobileMapViewOpen(false);
      if (!preselectedValues) {
        setProjectId("");
        setZoneId("");
        setBuildingId("");
        setSelectedSpace(null);
        setMapKey(0);
      }
    }
  }, [open, preselectedValues]);

  // Prepare options
  const projectsOptions =
    projectsData?.data?.map((project) => ({
      label: project.name,
      value: project.id,
    })) || [];

  const zonesOptions =
    zonesData?.data?.map((zone) => ({
      label: zone.name,
      value: zone.id,
      zone: zone,
    })) || [];

  const buildingsOptions =
    buildingsData?.data?.map((building) => ({
      label: building.name,
      value: building.id,
      building: building,
    })) || [];

  // Get current map data and items based on step
  const getCurrentMapData = () => {
    if (!currentMapData?.data) return null;

    if (buildingId) {
      return {
        mapData: currentMapData,
        loading: currentMapLoading,
        items: currentMapData.data.rooms || [],
        floorPlan: currentMapData.data.floorPlan,
        itemType: "space",
        title: trans("Level Map - Select Space"),
        instruction: trans(
          "Click on a space in the map or select from the dropdown above",
        ),
      };
    } else if (zoneId) {
      return {
        mapData: currentMapData,
        loading: currentMapLoading,
        items: currentMapData.data.rooms || [],
        floorPlan: currentMapData.data.floorPlan,
        itemType: "building",
        title: trans("Zone/Building Map - Select Level"),
        instruction: trans(
          "Click on a building in the map or select from the dropdown above",
        ),
      };
    } else if (projectId) {
      return {
        mapData: currentMapData,
        loading: currentMapLoading,
        items: currentMapData.data.rooms || [],
        floorPlan: currentMapData.data.floorPlan,
        itemType: "zone",
        title: trans("Project Map - Select Building/zone"),
        instruction: trans(
          "Click on a zone in the map or select from the dropdown above",
        ),
      };
    }
    return null;
  };

  const currentMap = getCurrentMapData();

  // Prepare dropdown options for space selection
  const getSpaceOptions = () => {
    if (buildingId && currentMap && currentMap.itemType === "space") {
      return (
        currentMap.items?.map((item) => ({
          label: item.displayName || item.code,
          value: item.poiId,
          item: item,
        })) || []
      );
    }
    return [];
  };

  const spaceOptions = getSpaceOptions();

  // Handle item selection from dropdown
  const handleItemSelect = (value, type) => {
    if (type === "project") {
      setProjectId(value);
    } else if (type === "zone") {
      setZoneId(value);
    } else if (type === "building") {
      setBuildingId(value);
    } else if (type === "space") {
      const spaceOption = spaceOptions.find((opt) => opt.value === value);
      if (spaceOption) {
        setSelectedSpace(spaceOption.item);
      }
    }
  };

  // Handle item click from map
  const handleItemClick = (item) => {
    if (currentMap?.itemType === "zone") {
      const zone = zonesOptions.find(
        (z) =>
          z.zone.code === item.code ||
          z.zone.name === item.name ||
          z.zone.name === item.displayName ||
          item.code === z.value?.toString() ||
          item.name === z.label,
      );
      if (zone) {
        setZoneId(zone.value);
      }
      if (isMobile && mobileMapViewOpen) setMobileMapViewOpen(false);
    } else if (currentMap?.itemType === "building") {
      const building = buildingsOptions.find(
        (b) =>
          b.building.code === item.code ||
          b.building.name === item.name ||
          b.building.name === item.displayName ||
          item.code === b.value?.toString() ||
          item.name === b.label,
      );
      if (building) {
        setBuildingId(building.value);
      }
      if (isMobile && mobileMapViewOpen) setMobileMapViewOpen(false);
    } else if (currentMap?.itemType === "space") {
      setSelectedSpace(item);
      if (isMobile && mobileMapViewOpen) {
        setMobileMapViewOpen(false);
      }
    }
  };

  /** Mobile overlay: select from list (used when map too large or for any step) */
  const handleMobileListSelect = (item) => {
    if (currentMap?.itemType === "zone") {
      const id = item.id ?? item.poiId;
      if (id) setZoneId(id);
    } else if (currentMap?.itemType === "building") {
      const id = item.id ?? item.poiId;
      if (id) setBuildingId(id);
    } else if (currentMap?.itemType === "space") {
      setSelectedSpace(item);
    }
    setMobileMapViewOpen(false);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (selectedSpace && onConfirm) {
      onConfirm({
        project_id: projectId,
        zone_id: zoneId,
        building_id: buildingId,
        level: selectedSpace.poiId,
        area_room: selectedSpace.code,
        location: selectedSpace.displayName || selectedSpace.code,
        project_name:
          projectsData?.data?.find((p) => p.id === projectId)?.name || "",
        zone_name: zonesData?.data?.find((z) => z.id === zoneId)?.name || "",
        building_name:
          buildingsData?.data?.find((b) => b.id === buildingId)?.name || "",
        space_name: selectedSpace.displayName || selectedSpace.code,
      });
      onClose();
    }
  };

  const canConfirm = selectedSpace !== null;

  // Calculate current step for progress
  const getCurrentStep = () => {
    if (selectedSpace) return 4;
    if (buildingId) return 3;
    if (zoneId) return 2;
    if (projectId) return 1;
    return 0;
  };

  // Build image URL
  const getImageUrl = (floorPlan) => {
    if (!floorPlan?.imageUrl) return null;
    return floorPlan.imageUrl;
  };

  const currentImageUrl = currentMap ? getImageUrl(currentMap.floorPlan) : null;
  const selectedItemCode =
    currentMap?.itemType === "space"
      ? selectedSpace?.code
      : currentMap?.itemType === "building"
        ? buildingId
        : currentMap?.itemType === "zone"
          ? zoneId
          : null;

  // Get selected names for breadcrumb display
  const getSelectedNames = () => {
    const project = projectsData?.data?.find((p) => p.id === projectId);
    const zone = zonesData?.data?.find((z) => z.id === zoneId);
    const building = buildingsData?.data?.find((b) => b.id === buildingId);

    return {
      project: project?.name,
      zone: zone?.name,
      building: building?.name,
      space: selectedSpace?.displayName || selectedSpace?.code,
    };
  };

  const selectedNames = getSelectedNames();

  // Lock body scroll when modal is open (so background doesn't scroll)
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    const prevPosition = document.body.style.position;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    if (isMobile) {
      document.body.style.position = "fixed";
      document.body.style.left = "0";
      document.body.style.right = "0";
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
      document.body.style.position = prevPosition;
      document.body.style.left = "";
      document.body.style.right = "";
    };
  }, [open, isMobile]);

  // On mobile, skip rendering the map if floor plan is too large (prevents crash)
  const floorPlan = currentMap?.floorPlan;
  const mapW = floorPlan?.width ?? 0;
  const mapH = floorPlan?.height ?? 0;
  const isMapTooLargeForMobile = false;
  // isMobile &&
  // (mapW * mapH > MOBILE_MAX_MAP_PIXELS ||
  //   mapW > MOBILE_MAX_MAP_SIDE ||
  //   mapH > MOBILE_MAX_MAP_SIDE);
  const shouldRenderMap = currentMap && !isMapTooLargeForMobile;

  /** Mobile: label for "Select from map" button by current step */
  const getMobileMapButtonLabel = () => {
    if (!currentMap) return "";
    if (isMapTooLargeForMobile) {
      if (currentMap.itemType === "zone")
        return trans("Select building from list");
      if (currentMap.itemType === "building")
        return trans("Select level from list");
      return trans("Select space from list");
    }
    if (currentMap.itemType === "zone")
      return trans("Select building from map");
    if (currentMap.itemType === "building")
      return trans("Select level from map");
    return trans("Select space from map");
  };

  /** Mobile: overlay title by step */
  const getMobileMapOverlayTitle = () => {
    if (!currentMap) return "";
    if (isMapTooLargeForMobile) {
      if (currentMap.itemType === "zone") return trans("Select building");
      if (currentMap.itemType === "building") return trans("Select level");
      return trans("Select space");
    }
    if (currentMap.itemType === "zone") return trans("Select building on map");
    if (currentMap.itemType === "building") return trans("Select level on map");
    return trans("Select space on map");
  };

  /** Mobile list: which item is selected (for highlight) */
  const getMobileListSelectedId = (item) => {
    if (!currentMap) return false;
    if (currentMap.itemType === "zone")
      return zoneId === (item.id ?? item.poiId);
    if (currentMap.itemType === "building")
      return buildingId === (item.id ?? item.poiId);
    return (
      selectedSpace?.poiId === item.poiId || selectedSpace?.code === item.code
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={isMobile ? "95%" : 1200}
        style={{ top: 10 }}
        zIndex={1060}
        getContainer={() => document.body}
        destroyOnClose
        className={`snag-location-modal ${isMobile ? "snag-location-modal-mobile" : ""}`}
        title={
          <PopupTitle
            headtitle={trans("Select Snag Location")}
            subtitle={trans(
              "Follow the steps below to pinpoint the exact location of the snag",
            )}
          />
        }
      >
        <div className="modal-content">
          {/* Help Banner */}
          {!isMobile && (
            <Alert
              message={trans("How it works")}
              description={trans(
                "Select each level step by step: Project → Building → Level → Space. You can either choose from the dropdowns or click directly on the map.",
              )}
              type="info"
              showIcon
              closable
              style={{ marginBottom: 20 }}
              className="help-banner"
            />
          )}
          {/* Progress Steps */}
          {/* <div className="progress-steps-wrapper">
          <Steps
            current={getCurrentStep()}
            size="small"
            items={[
              {
                title: trans("Project"),
                icon: <HomeOutlined />,
                description: projectId ? selectedNames.project : trans("Select"),
                status: projectId ? "finish" : getCurrentStep() === 0 ? "process" : "wait",
              },
              {
                title: trans("Building"),
                icon: <BuildOutlined />,
                description: zoneId ? selectedNames.zone : trans("Select"),
                status: zoneId ? "finish" : getCurrentStep() === 1 ? "process" : "wait",
              },
              {
                title: trans("Level"),
                icon: <ApartmentOutlined />,
                description: buildingId ? selectedNames.building : trans("Select"),
                status: buildingId ? "finish" : getCurrentStep() === 2 ? "process" : "wait",
              },
              {
                title: trans("Space"),
                icon: <EnvironmentOutlined />,
                description: selectedSpace ? selectedNames.space : trans("Select"),
                status: selectedSpace ? "finish" : getCurrentStep() === 3 ? "process" : "wait",
              },
            ]}
          />
        </div> */}

          {/* Selection Dropdowns with Icons */}
          <Card className="selection-card" bordered={false}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                {" "}
                <div className="input-with-icon">
                  <HomeOutlined className="input-icon" />
                  <InputSelect
                    id="location-project"
                    label={trans("Project")}
                    placeholder={trans("Choose project")}
                    options={projectsOptions}
                    value={projectId}
                    onChange={(val) => handleItemSelect(val, "project")}
                    formstyle="form-input"
                    showSearch
                  />
                </div>
                {!projectId && (
                  <div className="field-hint">
                    <InfoCircleOutlined /> {trans("Start here")}
                  </div>
                )}
              </Col>
              <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                {" "}
                <div className="input-with-icon">
                  <BuildOutlined className="input-icon" />
                  <InputSelect
                    id="location-zone"
                    label={trans("Building")}
                    placeholder={
                      projectId
                        ? trans("Choose building")
                        : trans("Select project first")
                    }
                    options={zonesOptions}
                    value={zoneId || ""}
                    onChange={(val) => handleItemSelect(val, "zone")}
                    formstyle="form-input"
                    disabled={!projectId}
                    loading={zonesLoading}
                    showSearch
                  />
                </div>
                {projectId &&
                  !zoneId &&
                  zonesOptions.length === 0 &&
                  !zonesLoading && (
                    <div className="field-hint warning">
                      <InfoCircleOutlined /> {trans("No buildings available")}
                    </div>
                  )}
              </Col>
              <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                {" "}
                <div className="input-with-icon">
                  <ApartmentOutlined className="input-icon" />
                  <InputSelect
                    id="location-building"
                    label={trans("Level")}
                    placeholder={
                      zoneId
                        ? trans("Choose level")
                        : trans("Select building first")
                    }
                    options={buildingsOptions}
                    value={buildingId || ""}
                    onChange={(val) => handleItemSelect(val, "building")}
                    formstyle="form-input"
                    disabled={!zoneId}
                    loading={buildingsLoading}
                    showSearch
                  />
                </div>
                {zoneId &&
                  !buildingId &&
                  buildingsOptions.length === 0 &&
                  !buildingsLoading && (
                    <div className="field-hint warning">
                      <InfoCircleOutlined /> {trans("No levels available")}
                    </div>
                  )}
              </Col>
              <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                {" "}
                <div className="input-with-icon">
                  <EnvironmentOutlined className="input-icon" />
                  <InputSelect
                    id="location-space"
                    label={trans("Space")}
                    placeholder={
                      buildingId
                        ? trans("Choose space")
                        : trans("Select level first")
                    }
                    options={spaceOptions}
                    value={selectedSpace?.poiId || ""}
                    onChange={(val) => handleItemSelect(val, "space")}
                    formstyle="form-input"
                    disabled={!buildingId}
                    showSearch
                    allowClear
                  />
                </div>
                {buildingId && !selectedSpace && spaceOptions.length === 0 && (
                  <div className="field-hint warning">
                    <InfoCircleOutlined /> {trans("No spaces available")}
                  </div>
                )}
              </Col>
            </Row>
          </Card>

          {/* Map Section */}
          {!currentMap && projectId && (
            <div className="empty-map-state">
              <EnvironmentOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />
              <Paragraph type="secondary" style={{ marginTop: 16 }}>
                {trans(
                  "Complete the selections above to view the interactive map",
                )}
              </Paragraph>
            </div>
          )}

          {currentMap && isMapTooLargeForMobile && (
            <Alert
              message={trans("Map too large for mobile")}
              description={trans(
                "This layout is too large to display on mobile. Please use the dropdowns above to select Project, Building, Level, and Space.",
              )}
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          {/* Desktop: inline map. Mobile: no inline map; use "Open map" button instead */}
          {shouldRenderMap && !isMobile && (
            <div className="map-section" key={mapKey}>
              <div className="map-header">
                <div className="map-title-row">
                  <Tag color="blue" icon={<EnvironmentOutlined />}>
                    {currentMap.itemType === "zone" &&
                      trans("Building Selection")}
                    {currentMap.itemType === "building" &&
                      trans("Level Selection")}
                    {currentMap.itemType === "space" &&
                      trans("Space Selection")}
                  </Tag>
                  <Text strong>{currentMap.title}</Text>
                </div>
                <Alert
                  message={currentMap.instruction}
                  type="info"
                  showIcon={false}
                  style={{
                    marginTop: 12,
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                  }}
                />
              </div>

              <div className="map-container-snag">
                <ImageMapViewer
                  imageUrl={currentImageUrl}
                  imageWidth={currentMap?.floorPlan?.width}
                  imageHeight={currentMap?.floorPlan?.height}
                  rooms={currentMap?.items || []}
                  selectedRoomCode={selectedItemCode}
                  onRoomClick={handleItemClick}
                  loading={currentMap?.loading}
                  isMobile={false}
                />
              </div>
            </div>
          )}

          {/* Mobile only: button to open map/list at each stage (map loads only there, avoids crash in form) */}
          {isMobile && currentMap && !mobileMapViewOpen && (
            <div className="mobile-open-map-section">
              <Button
                type="primary"
                size="large"
                icon={<EnvironmentOutlined />}
                onClick={() => setMobileMapViewOpen(true)}
                block
                className="mobile-open-map-btn"
              >
                {getMobileMapButtonLabel()}
              </Button>
              {(currentMap.itemType === "zone" &&
                zoneId &&
                selectedNames.zone) ||
              (currentMap.itemType === "building" &&
                buildingId &&
                selectedNames.building) ||
              (currentMap.itemType === "space" &&
                selectedSpace &&
                selectedNames.space) ? (
                <div className="mobile-selected-space-hint">
                  <CheckOutlined /> {trans("Selected")}:{" "}
                  {currentMap.itemType === "zone"
                    ? selectedNames.zone
                    : currentMap.itemType === "building"
                      ? selectedNames.building
                      : selectedNames.space}
                </div>
              ) : null}
            </div>
          )}

          {/* Selected Location Summary */}
          {selectedSpace && (
            <Card className="selection-summary" bordered={false}>
              <div className="summary-header">
                <CheckOutlined
                  style={{ color: "#52c41a", fontSize: 20, marginRight: 8 }}
                />
                <Text strong style={{ fontSize: 16 }}>
                  {trans("Selected Location")}
                </Text>
              </div>
              <div className="summary-content">
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <div className="summary-row">
                    <HomeOutlined style={{ color: "#1890ff" }} />
                    <Text type="secondary">{trans("Project")}:</Text>
                    <Text strong>{selectedNames.project}</Text>
                  </div>
                  <div className="summary-row">
                    <BuildOutlined style={{ color: "#1890ff" }} />
                    <Text type="secondary">{trans("Building")}:</Text>
                    <Text strong>{selectedNames.zone}</Text>
                  </div>
                  <div className="summary-row">
                    <ApartmentOutlined style={{ color: "#1890ff" }} />
                    <Text type="secondary">{trans("Level")}:</Text>
                    <Text strong>{selectedNames.building}</Text>
                  </div>
                  <div className="summary-row">
                    <EnvironmentOutlined style={{ color: "#52c41a" }} />
                    <Text type="secondary">{trans("Space")}:</Text>
                    <Text strong style={{ color: "#52c41a" }}>
                      {selectedNames.space}
                    </Text>
                  </div>
                </Space>
              </div>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div
          className={`modal-footer ${isMobile ? "modal-footer-mobile" : ""}`}
        >
          <div className="footer-actions">
            {isMobile && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleConfirm}
                disabled={!canConfirm}
                size="large"
              >
                {canConfirm
                  ? trans("Confirm Selection")
                  : trans("Complete Selection")}
              </Button>
            )}

            <Button onClick={onClose} size="large">
              {trans("Cancel")}
            </Button>
            {!isMobile && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleConfirm}
                disabled={!canConfirm}
                size="large"
              >
                {canConfirm
                  ? trans("Confirm Selection")
                  : trans("Complete Selection")}
              </Button>
            )}
          </div>
          {!canConfirm && (
            <Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              {trans("Please complete all selections to confirm")}
            </Text>
          )}
        </div>
      </Modal>

      {/* Mobile only: map/list overlay – fits page, scrollable; prefer landscape */}
      {open &&
        isMobile &&
        mobileMapViewOpen &&
        currentMap &&
        ReactDOM.createPortal(
          <div className="snag-location-mobile-map-overlay">
            <header className="snag-location-mobile-map-header">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setMobileMapViewOpen(false)}
                className="snag-location-mobile-map-back"
              />
              <span className="snag-location-mobile-map-title">
                {getMobileMapOverlayTitle()}
              </span>
            </header>
            <div className="snag-location-mobile-map-landscape-hint">
              <RotateRightOutlined />{" "}
              {trans("For best experience, use landscape orientation")}
            </div>
            <div className="snag-location-mobile-map-body">
              {isMapTooLargeForMobile ? (
                <div className="snag-location-mobile-map-list-wrap">
                  <List
                    dataSource={currentMap?.items || []}
                    renderItem={(item) => (
                      <List.Item
                        key={item.poiId || item.id || item.code}
                        onClick={() => handleMobileListSelect(item)}
                        className={
                          getMobileListSelectedId(item)
                            ? "snag-location-space-item selected"
                            : "snag-location-space-item"
                        }
                      >
                        {item.displayName || item.code || item.name}
                      </List.Item>
                    )}
                  />
                </div>
              ) : (
                <div className="snag-location-mobile-map-viewer">
                  <ImageMapViewer
                    key={mapKey}
                    imageUrl={currentImageUrl}
                    imageWidth={currentMap?.floorPlan?.width}
                    imageHeight={currentMap?.floorPlan?.height}
                    rooms={currentMap?.items || []}
                    selectedRoomCode={selectedItemCode}
                    onRoomClick={handleItemClick}
                    loading={currentMap?.loading}
                    isMobile={true}
                  />
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SnagLocationSelectorModal;
